import { createContext, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { request } from "../../shared/utils/network";

import { readToken } from '../../shared/auth/session';

export const EnvironmentsContext = createContext();

/**
 * The environments slice, split out of GlobalProvider (22 Aug 2026) so that
 * consumers of the active environment's variables stop re-rendering whenever
 * any of the ~90 unrelated global values change.
 *
 * Not to be confused with the Environments screen's feature-local
 * EnvironmentContext (src/features/environments), which holds that page's UI
 * state. This one carries the active environment used for variable
 * substitution app-wide:
 *
 * - environmentVariable: the active environment's variables, fetched whenever
 *   metaData.environment_id changes.
 * - refetchEnvironmentVariables: called by the Environments screen after an
 *   edit so the rest of the app sees fresh values.
 * - envName / selectedEnvironment: carried over from GlobalProvider unchanged.
 */
export const EnvironmentsProvider = ({ children }) => {
  const metaData = useSelector((state) => state.metaData);

  const [envName, setEnvName] = useState("");
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [environmentVariable, setEnvironmentVariable] = useState([]);

  // GlobalProvider had grown a mount effect and a refetch callback doing the
  // same request; here it is one callback, run on selection change and on
  // demand. The end state is unchanged — the same variables, keyed the same
  // way — which is what keeps the split safe for consumers.
  const refetchEnvironmentVariables = useCallback(async () => {
    const token = readToken();
    if (!metaData?.environment_id || !token) {
      return;
    }
    try {
      const response = await request(
        `/api/environment/${metaData.environment_id}`,
        "GET"
      );
      setEnvironmentVariable(response?.data?.variables || []);
    } catch (err) {
      console.error("EnvironmentsProvider - error fetching environment variables:", err);
    }
  }, [metaData?.environment_id]);

  useEffect(() => {
    refetchEnvironmentVariables();
  }, [refetchEnvironmentVariables]);

  return (
    <EnvironmentsContext.Provider
      value={{
        envName,
        setEnvName,
        selectedEnvironment,
        setSelectedEnvironment,
        environmentVariable,
        setEnvironmentVariable,
        refetchEnvironmentVariables,
      }}
    >
      {children}
    </EnvironmentsContext.Provider>
  );
};
